import { useTranslation } from 'react-i18next';
import { TenantOwnTerms, TenantPayments } from '../../api/account/accountApiTypes';
import { resolveBookingPageTerms } from './bookingPageTerms';
import OwnTermsControl from './OwnTermsControl';

interface Props {
    organizationName?: string | null;
    services?: string[] | null;
    payments?: TenantPayments | null;
    /** `config.contact.website` — the fallback the booking page links to. */
    website?: string | null;
    /** The tenant's own terms documents, if it supplied any (upload or link each). */
    ownTerms?: TenantOwnTerms;
    /** No writes — a member who is not the owner. */
    readOnly: boolean;
    /** A master on another tenant: the own terms are written by slug (PO, 2026-10-30). */
    masterSlug?: string;
}

/**
 * Which terms and conditions the tenant's booking page shows its customers.
 *
 * ── The one control: the tenant's own AGB (ADR-0003/0005, 2026-10-02) ──────
 *
 * "Does this tenant use its own AGB?" is answered nowhere else, so it is set
 * here, beside its visible consequence — see `OwnTermsControl`. Everything
 * below stays derived.
 *
 * ── Why the rest is read-only ──────────────────────────────────────────────
 *
 * Nothing else here is a switch, because nothing else is a separate decision. The
 * document follows from the use cases the tenant offers; whether it is the free
 * variant follows from `charging`, answered one tab away under Payment
 * settings; and who the customer's counterparty is follows from the tenant
 * record itself (PO, 2026-09-14). A control here would be a second place to
 * answer questions that already have an answer — and the two would drift.
 *
 * So this section exists to make the *consequence* visible: a tenant should be
 * able to see which contract its customers are shown without opening the
 * booking page, and should be told plainly that kolula supplies the wording
 * while the tenant carries it.
 *
 * ── Why Services and not Account ───────────────────────────────────────────
 *
 * ADR-0005: Account holds the tenant's identity and anything gating-adjacent;
 * Services holds per-use-case configuration. The document VARIES BY USE CASE —
 * that is what makes it Services', and it belongs on the usage-flow tab beside
 * booking-page activation, because it is part of what the live page presents
 * (PO, 2026-09-14).
 */
const BookingPageLegalSection = ({
    organizationName, services, payments, website, ownTerms, readOnly, masterSlug,
}: Props) => {
    const { t } = useTranslation();
    const terms = resolveBookingPageTerms({
        organizationName, services, payments, ownTerms: ownTerms?.documents ?? [],
    });

    // `service_type_selector` is where use-case labels already live — the same
    // source the sections above this one read.
    const useCaseLabel = (service: string) =>
        t(`service_type_selector.${service}`, { defaultValue: service });

    return (
        <section id={'legal-documents'} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                {t('services.legal_documents_section')}
            </h2>
            <p className={'text-sm text-foreground/60 mb-4'}>
                {t('services.legal_documents_desc')}
            </p>

            {/* The disclaimer leads rather than trails: kolula supplies the
                wording, the tenant is the one bound by it. Only where kolula
                does supply it — over the tenant's own AGB it would be false. */}
            {terms.document === 'station' && (
                <p className={'text-sm text-foreground/70 mb-4 border border-foreground/15'
                    + ' rounded-[5px] p-3'}
                >
                    {t('services.legal_documents_no_legal_advice')}
                </p>
            )}

            <dl className={'flex flex-col gap-3 text-sm'}>
                <div>
                    <dt className={'text-foreground/60'}>
                        {t('services.legal_documents_shown')}
                    </dt>
                    <dd className={'font-medium text-foreground'}>
                        {terms.document === 'own' && t('services.legal_documents_own')}
                        {terms.document === 'station' && t('services.legal_documents_station', {
                            variant: terms.paid
                                ? t('services.legal_documents_variant_paid')
                                : t('services.legal_documents_variant_free'),
                        })}
                        {terms.document === 'website' && t('services.legal_documents_website')}
                    </dd>
                    {terms.document === 'website' && !website && (
                        <dd className={'text-foreground/70 mt-1'}>
                            {t('services.legal_documents_website_missing')}
                        </dd>
                    )}
                </div>

                {terms.document === 'station' && terms.useCases.length > 0 && (
                    <div>
                        <dt className={'text-foreground/60'}>
                            {t('services.legal_documents_covers')}
                        </dt>
                        <dd className={'font-medium text-foreground'}>
                            {terms.useCases.map(useCaseLabel).join(', ')}
                        </dd>
                    </div>
                )}

                {terms.document !== 'own' && terms.uncovered.length > 0 && (
                    <div>
                        <dt className={'text-foreground/60'}>
                            {t('services.legal_documents_uncovered')}
                        </dt>
                        <dd className={'font-medium text-foreground'}>
                            {terms.uncovered.map(useCaseLabel).join(', ')}
                        </dd>
                    </div>
                )}

                <div>
                    <dt className={'text-foreground/60'}>
                        {t('services.legal_documents_counterparty')}
                    </dt>
                    <dd className={'font-medium text-foreground'}>
                        {terms.counterparty === 'kolula'
                            ? t('services.legal_documents_counterparty_kolula')
                            : organizationName || t('common.not_applicable')}
                    </dd>
                </div>
            </dl>

            <OwnTermsControl ownTerms={ownTerms} readOnly={readOnly} masterSlug={masterSlug} />
        </section>
    );
};

export default BookingPageLegalSection;
